// Tour Management Service
// This service handles all tour data operations with database-first approach and caching
// Supports both Supabase (main branch) and new backend API (this branch)

import { Tour, Review } from '../types';
import { databaseTourService } from './databaseTourService';
import { backendTourService } from './backendTourService';
import { USE_BACKEND_API } from '../config/apiConfig';
import { supabase } from './supabaseService';

// Database-first tour service with caching
// Automatically uses backend API if REACT_APP_USE_BACKEND_API=true, otherwise uses Supabase
class TourService {
  // Get all tours (database-first with caching)
  async getAllTours(forceRefresh: boolean = false): Promise<Tour[]> {
    if (USE_BACKEND_API) {
      return await backendTourService.getAllTours(forceRefresh);
    }
    return await databaseTourService.getAllTours(forceRefresh);
  }

  // Get only active tours
  async getActiveTours(forceRefresh: boolean = false): Promise<Tour[]> {
    if (USE_BACKEND_API) {
      const allTours = await backendTourService.getAllTours(forceRefresh);
      // Include active tours OR suspended tours (Coming Soon)
      return allTours.filter(tour => tour.active || tour.status === 'suspended');
    }
    return await databaseTourService.getActiveTours();
  }

  // Get tour by ID
  async getTourById(id: string): Promise<Tour | null> {
    if (USE_BACKEND_API) {
      return await backendTourService.getTourById(id);
    }
    return await databaseTourService.getTourById(id);
  }

  // Get full tour by ID (bypasses listing cache to ensure heavy JSON fields are present)
  async getFullTourById(id: string): Promise<Tour | null> {
    if (USE_BACKEND_API) {
      if (typeof backendTourService.getFullTourById === 'function') {
        return await backendTourService.getFullTourById(id);
      }
      return await backendTourService.getTourById(id);
    }
    return await databaseTourService.getTourById(id);
  }

  // Add new tour
  async addTour(tourData: Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>): Promise<Tour> {
    if (USE_BACKEND_API) {
      const newTour = await backendTourService.addTour(tourData);

      // Update guide rating after adding tour - only if guide ID is present and valid
      if (newTour.guides && newTour.guides.length > 0) {
        try {
          const { guideService } = await import('./guideService');
          for (const g of newTour.guides) {
            if (g.id && !g.id.startsWith('guide_')) {
              await guideService.updateGuideRating(g.id);
            }
          }
        } catch (error) {
          console.error('Error updating guide rating after tour creation:', error);
        }
      }

      return newTour;
    }

    const newTour = await databaseTourService.addTour(tourData);

    // Update guide rating after adding tour
    if (newTour.guides && newTour.guides.length > 0) {
      try {
        const { guideService } = await import('./guideService');
        for (const g of newTour.guides) {
          if (g.id && !g.id.startsWith('guide_')) {
            await guideService.updateGuideRating(g.id);
          }
        }
      } catch (error) {
        console.error('Error updating guide rating after tour creation:', error);
      }
    }

    return newTour;
  }

  // Update existing tour
  async updateTour(id: string, tourData: Partial<Tour>): Promise<Tour | null> {
    if (USE_BACKEND_API) {
      const updatedTour = await backendTourService.updateTour(id, tourData);

      // Update guide rating after updating tour - only if guide ID is present and valid
      const updateFields = Object.keys(tourData);
      const isStatusOrOrderUpdate = updateFields.every(field => ['status', 'sortOrder', 'sort_order'].includes(field));

      if (updatedTour && updatedTour.guides && Array.isArray(updatedTour.guides) && !isStatusOrOrderUpdate) {
        try {
          const { guideService } = await import('./guideService');
          for (const g of updatedTour.guides) {
            if (g && typeof g === 'object' && g.id && !g.id.startsWith('guide_')) {
              await guideService.updateGuideRating(g.id);
            }
          }
        } catch (error) {
          console.error('Error updating guide rating after tour update:', error);
        }
      }

      return updatedTour;
    }

    const updatedTour = await databaseTourService.updateTour(id, tourData as Omit<Tour, 'id' | 'createdAt' | 'updatedAt'>);

    // Update guide rating after updating tour
    if (updatedTour && updatedTour.guides && updatedTour.guides.length > 0) {
      try {
        const { guideService } = await import('./guideService');
        for (const g of updatedTour.guides) {
          if (g.id) {
            await guideService.updateGuideRating(g.id);
          }
        }
      } catch (error) {
        console.error('Error updating guide rating after tour update:', error);
      }
    }

    return updatedTour;
  }

  // Delete tour
  async deleteTour(id: string): Promise<boolean> {
    if (USE_BACKEND_API) {
      return await backendTourService.deleteTour(id);
    }
    return await databaseTourService.deleteTour(id);
  }

  // Get tours by category name (with case-insensitive matching)
  async getToursByCategory(category: string): Promise<Tour[]> {
    const allTours = await this.getAllTours();

    let categoryId: string | null = null;
    try {
      const { categoryService } = await import('./categoryService');
      const allCategories = await categoryService.getAllCategories();
      const matchedCategory = allCategories.find(cat =>
        cat.name.toLowerCase() === category.toLowerCase()
      );
      if (matchedCategory) {
        categoryId = matchedCategory.id;
      }
    } catch (error) {
      console.warn('Could not load categories for categoryId matching:', error);
    }

    return allTours.filter(tour => {
      const tourCategoryId = (tour as any).categoryId;
      // Prefer categoryId; fall back to name only when categoryId is absent
      if (tourCategoryId) return categoryId ? tourCategoryId === categoryId : false;
      return tour.category?.toLowerCase() === category.toLowerCase();
    });
  }

  // Get tours by category ID (with fallback to category name matching)
  async getToursByCategoryId(categoryId: string): Promise<Tour[]> {
    if (USE_BACKEND_API) {
      return await backendTourService.getToursByCategoryId(categoryId);
    }

    const allTours = await this.getAllTours();

    let categoryName: string | null = null;
    try {
      const { categoryService } = await import('./categoryService');
      const category = await categoryService.getCategoryById(categoryId);
      if (category) {
        categoryName = category.name;
      }
    } catch (error) {
      console.warn('Could not load category name for fallback matching:', error);
    }

    return allTours.filter(tour => {
      const tourCategoryId = (tour as any).categoryId;
      if (tourCategoryId) return tourCategoryId === categoryId;
      // Fallback: match by name only when the tour has no categoryId yet
      if (categoryName && tour.category) {
        return tour.category.toLowerCase() === categoryName.toLowerCase();
      }
      return false;
    });
  }

  // Get tour statistics
  async getTourStats(): Promise<{
    totalTours: number;
    activeTours: number;
    totalRevenue: number;
    avgRating: number;
  }> {
    if (USE_BACKEND_API) {
      return await backendTourService.getTourStats();
    }
    return await databaseTourService.getTourStats();
  }

  // Clear cache
  clearCache(): void {
    if (USE_BACKEND_API) {
      backendTourService.clearCache();
    }
    databaseTourService.clearCache();
  }

  // Force refresh cache
  async refreshCache(): Promise<Tour[]> {
    return await databaseTourService.refreshCache();
  }

  // Cache state exposure for instant UI
  hasCache(): boolean {
    if (USE_BACKEND_API) {
      return (backendTourService as any).hasCache?.() || false;
    }
    return false; // Supabase doesn't have a simple cache yet
  }

  getCache(): Tour[] {
    if (USE_BACKEND_API) {
      return (backendTourService as any).getCache?.() || [];
    }
    return [];
  }

  // Get reviews for a tour
  async getTourReviews(tourId: string): Promise<Review[]> {
    if (USE_BACKEND_API) {
      return await backendTourService.getTourReviews(tourId);
    }

    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('tour_id', tourId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching reviews:', error);
        return [];
      }

      return (data || []).map((review: any) => ({
        id: review.id,
        tourId: review.tour_id,
        userId: review.user_id || '',
        userName: review.user_name,
        rating: review.rating,
        title: review.title,
        comment: review.comment,
        images: review.images || [],
        createdAt: review.created_at,
        helpful: review.helpful || 0
      }));
    } catch (error) {
      console.error('Database error fetching reviews:', error);
      return [];
    }
  }

  // Add a review for a tour
  async addReview(tourId: string, reviewData: {
    userName: string;
    rating: number;
    title: string;
    comment: string;
  }): Promise<Review> {
    if (USE_BACKEND_API) {
      return await backendTourService.addReview(tourId, reviewData);
    }

    try {
      const { data, error } = await supabase
        .from('reviews')
        .insert({
          tour_id: tourId,
          user_name: reviewData.userName,
          rating: reviewData.rating,
          title: reviewData.title,
          comment: reviewData.comment
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding review:', error);
        throw new Error(`Failed to add review: ${error.message}`);
      }

      const review: Review = {
        id: data.id,
        tourId: data.tour_id,
        userId: data.user_id || '',
        userName: data.user_name,
        rating: data.rating,
        title: data.title,
        comment: data.comment,
        images: data.images || [],
        createdAt: data.created_at,
        helpful: data.helpful || 0
      };

      return review;
    } catch (error) {
      console.error('Database error adding review:', error);
      throw error;
    }
  }

  // Update tour rating based on reviews
  async updateTourRating(tourId: string): Promise<void> {
    if (USE_BACKEND_API) {
      await backendTourService.updateTourRating(tourId);
    } else {
      try {
        const { data: reviews, error } = await supabase
          .from('reviews')
          .select('rating')
          .eq('tour_id', tourId);

        if (error) {
          console.error('Error fetching reviews for rating update:', error);
        } else if (reviews && reviews.length > 0) {
          const avgRating = reviews.reduce((sum: any, r: any) => sum + r.rating, 0) / reviews.length;
          const reviewCount = reviews.length;

          const { error: updateError } = await supabase
            .from('tours')
            .update({
              rating: Math.round(avgRating * 10) / 10,
              review_count: reviewCount
            })
            .eq('id', tourId);

          if (updateError) {
            console.error('Error updating tour rating:', updateError);
          }
        }
      } catch (error) {
        console.error('Database error updating tour rating:', error);
      }
    }

    // Propagate rating update to guides regardless of which backend was used
    try {
      const tour = await this.getTourById(tourId);
      if (tour && tour.guides && tour.guides.length > 0) {
        const { guideService } = await import('./guideService');
        for (const g of tour.guides) {
          if (g.id && !g.id.startsWith('guide_')) {
            await guideService.updateGuideRating(g.id);
          }
        }
      }
    } catch (error) {
      console.error('Error propagating rating update to guides:', error);
    }
  }
}

export const tourService = new TourService();
export default tourService;